import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  AiDiagnoseRequest,
  AiDiagnoseResponse,
  AiQueryRequest,
  AiQueryResponse,
  CreatePartInput,
  CreatePartMovementInput,
  CreatePartRequestInput,
  CreatePreventiveMaintenanceInput,
  CreateServiceOrderInput,
  CreateVehicleFeatureInput,
  CreateVehicleInput,
  Dashboard,
  InvoiceExtractRequest,
  InvoiceExtractResponse,
  InvoiceMatchResponse,
  Part,
  PartLinkEnrichRequest,
  PartLinkEnrichResponse,
  PartMovement,
  PartRequestWithContext,
  PartToStockInput,
  PreventiveMaintenance,
  RecentEmailsResponse,
  ServiceOrder,
  ServiceOrderStatus,
  ServiceOrderWithDerived,
  TrackShipmentRequest,
  TrackShipmentResponse,
  UpdatePartRequestInput,
  UpdateVehicleInput,
  User,
  Vehicle,
  VehicleFeature,
} from '@etnrlz/shared';
import { api } from './api';

export const qk = {
  dashboard: ['dashboard'] as const,
  vehicles: ['vehicles'] as const,
  vehicle: (id: string) => ['vehicles', id] as const,
  features: (id: string) => ['vehicles', id, 'features'] as const,
  vehicleOrders: (id: string) => ['vehicles', id, 'orders'] as const,
  preventive: (id: string) => ['vehicles', id, 'preventive'] as const,
  orders: (f?: Record<string, string | undefined>) => ['orders', f ?? {}] as const,
  order: (id: string) => ['orders', id] as const,
  parts: (q?: Record<string, string | undefined>) => ['parts', q ?? {}] as const,
  part: (id: string) => ['parts', id] as const,
  movements: (id: string) => ['parts', id, 'movements'] as const,
  due: ['preventive', 'due'] as const,
  expiring: ['features', 'expiring'] as const,
  users: ['users'] as const,
  partRequests: ['part-requests'] as const,
};

/* ------------------------------- Dashboard ------------------------------- */
export function useDashboard(refetchMs?: number) {
  return useQuery({
    queryKey: qk.dashboard,
    queryFn: () => api.get<Dashboard>('/dashboard'),
    refetchInterval: refetchMs,
  });
}

/* -------------------------------- Vehicles ------------------------------- */
export const useVehicles = () =>
  useQuery({ queryKey: qk.vehicles, queryFn: () => api.get<Vehicle[]>('/vehicles') });

export const useVehicle = (id: string, opts?: Partial<UseQueryOptions<Vehicle>>) =>
  useQuery({ queryKey: qk.vehicle(id), queryFn: () => api.get<Vehicle>(`/vehicles/${id}`), ...opts });

export function useCreateVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVehicleInput) => api.post<Vehicle>('/vehicles', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vehicles }),
  });
}
export function useUpdateVehicle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateVehicleInput) => api.put<Vehicle>(`/vehicles/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vehicles });
      qc.invalidateQueries({ queryKey: qk.vehicle(id) });
    },
  });
}
export function useDeleteVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/vehicles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vehicles }),
  });
}

/* -------------------------------- Features ------------------------------- */
export const useFeatures = (vehicleId: string) =>
  useQuery({
    queryKey: qk.features(vehicleId),
    queryFn: () => api.get<VehicleFeature[]>(`/vehicles/${vehicleId}/features`),
  });

export function useCreateFeature(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVehicleFeatureInput) =>
      api.post<VehicleFeature>(`/vehicles/${vehicleId}/features`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.features(vehicleId) }),
  });
}
export function useDeleteFeature(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fid: string) => api.del(`/vehicles/${vehicleId}/features/${fid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.features(vehicleId) }),
  });
}

/* ----------------------------- ServiceOrders ----------------------------- */
export const useServiceOrders = (filter?: { status?: string; mechanic?: string; vehicle?: string }) =>
  useQuery({
    queryKey: qk.orders(filter),
    queryFn: () => api.get<ServiceOrderWithDerived[]>('/service-orders', { query: filter }),
  });

export const useServiceOrder = (id: string) =>
  useQuery({
    queryKey: qk.order(id),
    queryFn: () => api.get<ServiceOrderWithDerived>(`/service-orders/${id}`),
  });

export const useVehicleOrders = (vehicleId: string) =>
  useQuery({
    queryKey: qk.vehicleOrders(vehicleId),
    queryFn: () => api.get<ServiceOrderWithDerived[]>(`/vehicles/${vehicleId}/service-orders`),
  });

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateServiceOrderInput) => api.post<ServiceOrder>('/service-orders', input),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.dashboard });
      qc.invalidateQueries({ queryKey: qk.vehicleOrders(input.vehicleId) });
    },
  });
}
export function useChangeOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, nota }: { id: string; status: ServiceOrderStatus; nota?: string }) =>
      api.patch<ServiceOrder>(`/service-orders/${id}/status`, { status, nota }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}
export function useUpdateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ServiceOrder> }) =>
      api.put<ServiceOrder>(`/service-orders/${id}`, input),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.order(v.id) });
    },
  });
}
export function useUpdateChecklist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (checklist: ServiceOrder['checklist']) =>
      api.put<ServiceOrder>(`/service-orders/${id}/checklist`, { checklist }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.order(id) });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
export function useAddComment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (texto: string) => api.post<ServiceOrder>(`/service-orders/${id}/comments`, { texto }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.order(id) }),
  });
}
export function useRemoveComment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cid: string) => api.del<ServiceOrder>(`/service-orders/${id}/comments/${cid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.order(id) }),
  });
}

/* -------------------------------- Parts ---------------------------------- */
export const useParts = (query?: { q?: string; category?: string }) =>
  useQuery({ queryKey: qk.parts(query), queryFn: () => api.get<Part[]>('/parts', { query }) });

export const usePart = (id: string) =>
  useQuery({ queryKey: qk.part(id), queryFn: () => api.get<Part>(`/parts/${id}`) });

export const useMovements = (id: string) =>
  useQuery({ queryKey: qk.movements(id), queryFn: () => api.get<PartMovement[]>(`/parts/${id}/movements`) });

export function useCreatePart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartInput) => api.post<Part>('/parts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts'] }),
  });
}
export function useUpdatePart(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Part>) => api.put<Part>(`/parts/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts'] });
      qc.invalidateQueries({ queryKey: qk.part(id) });
    },
  });
}
export function useAddMovement(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartMovementInput) =>
      api.post<{ movement: PartMovement; part: Part }>(`/parts/${partId}/movements`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts'] });
      qc.invalidateQueries({ queryKey: qk.movements(partId) });
      qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}
export const lookupPart = (qrValue: string) =>
  api.get<Part>(`/parts/lookup/${encodeURIComponent(qrValue)}`);

/* ------------------------------ Preventive ------------------------------- */
export const usePreventive = (vehicleId: string) =>
  useQuery({
    queryKey: qk.preventive(vehicleId),
    queryFn: () => api.get<PreventiveMaintenance[]>(`/vehicles/${vehicleId}/preventive`),
  });

export function useCreatePreventive(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePreventiveMaintenanceInput) =>
      api.post<PreventiveMaintenance>(`/vehicles/${vehicleId}/preventive`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.preventive(vehicleId) }),
  });
}

/* --------------------------------- Users --------------------------------- */
export const useUsers = () =>
  useQuery({ queryKey: qk.users, queryFn: () => api.get<User[]>('/users') });

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }),
  });
}

/* ---------------------------------- IA ----------------------------------- */
export function useAiQuery() {
  return useMutation({
    mutationFn: (req: AiQueryRequest) => api.post<AiQueryResponse>('/ai/query', req),
  });
}
export function useAiDiagnose() {
  return useMutation({
    mutationFn: (req: AiDiagnoseRequest) => api.post<AiDiagnoseResponse>('/ai/diagnose', req),
  });
}
/** Carrega dados do produto (título/descrição/valor/foto) a partir do link. */
export function useEnrichPartFromLink() {
  return useMutation({
    mutationFn: (req: PartLinkEnrichRequest) =>
      api.post<PartLinkEnrichResponse>('/ai/part-from-link', req),
  });
}
/** Estima transportadora/prazo de entrega a partir do código de rastreio. */
export function useTrackShipment() {
  return useMutation({
    mutationFn: (req: TrackShipmentRequest) =>
      api.post<TrackShipmentResponse>('/ai/track-shipment', req),
  });
}
/** Lê uma invoice (PDF/imagem) e extrai OrderID, frete e valores (Opus 4.8). */
export function useExtractInvoice() {
  return useMutation({
    mutationFn: (req: InvoiceExtractRequest) =>
      api.post<InvoiceExtractResponse>('/ai/invoice-extract', req),
  });
}
/** Lê uma invoice/NF e casa cada item com as peças cadastradas (Opus 4.8). */
export function useMatchInvoice() {
  return useMutation({
    mutationFn: (req: InvoiceExtractRequest) =>
      api.post<InvoiceMatchResponse>('/ai/invoice-match', req),
  });
}

/* ------------------------- Solicitação de peças -------------------------- */
export function useAddPartRequest(osId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartRequestInput) =>
      api.post<ServiceOrder>(`/service-orders/${osId}/part-requests`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.order(osId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.partRequests });
    },
  });
}
export function useUpdatePartRequest(osId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prId, input }: { prId: string; input: UpdatePartRequestInput }) =>
      api.put<ServiceOrder>(`/service-orders/${osId}/part-requests/${prId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.order(osId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.partRequests });
    },
  });
}
export function useRemovePartRequest(osId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prId: string) =>
      api.del<ServiceOrder>(`/service-orders/${osId}/part-requests/${prId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.order(osId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.partRequests });
    },
  });
}
/** Move uma peça já comprada de outra OS para esta. */
export function useMovePartRequest(osId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { fromOsId: string; prId: string }) =>
      api.post<ServiceOrder>(`/service-orders/${osId}/part-requests/move`, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.partRequests });
    },
  });
}
/** Lança uma peça entregue no estoque (cria item de inventário). */
export function usePartRequestToStock(osId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prId, input }: { prId: string; input: PartToStockInput }) =>
      api.post<Part>(`/service-orders/${osId}/part-requests/${prId}/to-stock`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.order(osId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: qk.partRequests });
      qc.invalidateQueries({ queryKey: ['parts'] });
    },
  });
}

/* ----------------------------- Compras (peças) --------------------------- */
/** Todas as peças solicitadas (todas as OS) com contexto — área de compras. */
export const usePartRequests = () =>
  useQuery({
    queryKey: qk.partRequests,
    queryFn: () => api.get<PartRequestWithContext[]>('/part-requests'),
  });

/** Últimos e-mails da caixa parts@ (read-only) — para a tela de Compras. */
export const useRecentEmails = () =>
  useQuery({
    queryKey: ['emails-recent'],
    queryFn: () => api.get<RecentEmailsResponse>('/emails/recent'),
    staleTime: 5 * 60 * 1000,
  });

/** Atualiza uma peça de qualquer OS (área de compras) — precisa de osId + prId. */
export function useUpdatePartRequestAny() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      osId,
      prId,
      input,
    }: {
      osId: string;
      prId: string;
      input: UpdatePartRequestInput;
    }) => api.put<ServiceOrder>(`/service-orders/${osId}/part-requests/${prId}`, input),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: qk.partRequests });
      qc.invalidateQueries({ queryKey: qk.order(v.osId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
